import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Info, TrendingUp, ShieldCheck, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TEAM_LABELS = {
  medical_mdo: "Medisch MDO",
  legal_compliance: "Juridisch & Compliance",
  crisis_incident: "Crisis & Incident",
  construction_infra: "Bouw / Infra / Omgeving",
};

const METRICS = [
  {
    id: "ds",
    label: "Disagreement",
    full: "Disagreement Score (DS)",
    icon: TrendingUp,
    description: "Hoe vaak agents echt botsen i.p.v. polderen. 0 = consensus, 1 = continue clash.",
    target: "≥ 0.40",
    invert: false,
  },
  {
    id: "aa",
    label: "Anchor adherence",
    full: "Anchor Adherence (AA)",
    icon: ShieldCheck,
    description: "Blijft elke specialist trouw aan zijn vakgebied? 0 = inwisselbaar, 1 = strikt in rol.",
    target: "≥ 0.75",
    invert: false,
  },
  {
    id: "si",
    label: "Sycophancy",
    full: "Sycophancy Index (SI)",
    icon: AlertCircle,
    description: "Politesse-padding ('goede vraag', 'ik ben het eens met'). LAGER = beter.",
    target: "≤ 0.15",
    invert: true,
  },
];

const scoreToColor = (value, invert = false) => {
  if (invert) return value <= 0.15 ? "text-green-600" : value <= 0.3 ? "text-amber-600" : "text-red-600";
  return value >= 0.7 ? "text-green-600" : value >= 0.4 ? "text-amber-600" : "text-red-600";
};

const scoreToBg = (value, invert = false) => {
  if (invert) return value <= 0.15 ? "bg-green-500" : value <= 0.3 ? "bg-amber-500" : "bg-red-500";
  return value >= 0.7 ? "bg-green-500" : value >= 0.4 ? "bg-amber-500" : "bg-red-500";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
};

export const TrustMetrics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/quality/latest`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "Scores konden niet worden geladen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="my-12 border-t-2 border-gray-200 pt-10" data-testid="trust-metrics-loading">
        <div className="flex justify-center items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Live scores laden…
        </div>
      </section>
    );
  }

  if (error || !data || !data.summary || Object.keys(data.summary).length === 0) {
    // No public metrics yet — show nothing (don't expose internal scaffolding)
    return null;
  }

  const teams = Object.entries(data.summary);
  const aggregate = teams.reduce(
    (acc, [, s]) => ({
      ds: acc.ds + s.avg_ds,
      aa: acc.aa + s.avg_aa,
      si: acc.si + s.avg_si_llm,
      n: acc.n + (s.n || 0),
    }),
    { ds: 0, aa: 0, si: 0, n: 0 }
  );
  const overall = {
    ds: teams.length ? aggregate.ds / teams.length : 0,
    aa: teams.length ? aggregate.aa / teams.length : 0,
    si: teams.length ? aggregate.si / teams.length : 0,
    n: aggregate.n,
  };

  // Find latest scored_at across all dilemmas
  let latestDate = null;
  teams.forEach(([, s]) => {
    (s.dilemmas || []).forEach((d) => {
      if (!latestDate || (d.scored_at && d.scored_at > latestDate)) latestDate = d.scored_at;
    });
  });

  return (
    <section className="my-12 border-t-2 border-gray-200 pt-10" data-testid="trust-metrics-section">
      <div className="text-center mb-6">
        <p className="text-[11px] font-mono uppercase tracking-widest text-gray-500 mb-2">Live kwaliteits-meting</p>
        <h2 className="text-2xl sm:text-3xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>
          Wij meten onszelf. Hier zijn de scores.
        </h2>
        <p className="text-sm text-gray-600 mt-2 max-w-2xl mx-auto">
          Iedere week draaien we {aggregate.n} dilemma&apos;s door onze B2B-teams en scoren ze automatisch op disagreement,
          discipline-trouw en politesse-padding. De cijfers hieronder zijn niet gepoetst — we tonen wat de meting oplevert.
        </p>
      </div>

      {/* Aggregate scores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="trust-aggregate">
        {METRICS.map((m) => {
          const value = overall[m.id];
          return (
            <div key={m.id} className="border-2 border-gray-200 bg-white p-5" data-testid={`trust-metric-${m.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <m.icon className={`w-4 h-4 ${scoreToColor(value, m.invert)}`} />
                  <span className="text-xs font-mono uppercase text-gray-500">{m.label}</span>
                </div>
                <span className="text-[10px] font-mono text-gray-400">streef {m.target}</span>
              </div>
              <p className={`text-4xl font-black ${scoreToColor(value, m.invert)}`} style={{ fontFamily: "Chivo, sans-serif" }}>
                {value.toFixed(2)}
              </p>
              <div className="h-1.5 bg-gray-100 mt-2 mb-3">
                <div className={`h-full ${scoreToBg(value, m.invert)}`} style={{ width: `${Math.round(value * 100)}%` }} />
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed">{m.description}</p>
            </div>
          );
        })}
      </div>

      {/* Per-team breakdown */}
      <div className="border-2 border-gray-200 bg-white overflow-hidden" data-testid="trust-team-breakdown">
        <div className="grid grid-cols-12 bg-gray-900 text-white text-[10px] font-bold uppercase tracking-wider">
          <div className="col-span-6 sm:col-span-5 px-3 py-2">Team</div>
          <div className="col-span-2 sm:col-span-2 px-2 py-2 text-center">DS ↑</div>
          <div className="col-span-2 sm:col-span-2 px-2 py-2 text-center">AA ↑</div>
          <div className="col-span-2 sm:col-span-2 px-2 py-2 text-center">SI ↓</div>
          <div className="hidden sm:block sm:col-span-1 px-2 py-2 text-center">n</div>
        </div>
        {teams.map(([tid, s]) => (
          <div key={tid} className="grid grid-cols-12 text-xs border-t border-gray-100" data-testid={`trust-team-${tid}`}>
            <div className="col-span-6 sm:col-span-5 px-3 py-2.5 font-bold">{TEAM_LABELS[tid] || tid}</div>
            <div className={`col-span-2 sm:col-span-2 px-2 py-2.5 text-center font-mono ${scoreToColor(s.avg_ds)}`}>
              {s.avg_ds.toFixed(2)}
            </div>
            <div className={`col-span-2 sm:col-span-2 px-2 py-2.5 text-center font-mono ${scoreToColor(s.avg_aa)}`}>
              {s.avg_aa.toFixed(2)}
            </div>
            <div className={`col-span-2 sm:col-span-2 px-2 py-2.5 text-center font-mono ${scoreToColor(s.avg_si_llm, true)}`}>
              {s.avg_si_llm.toFixed(2)}
            </div>
            <div className="hidden sm:block sm:col-span-1 px-2 py-2.5 text-center font-mono text-gray-500">
              {s.n}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-2 mt-4 text-[11px] text-gray-500">
        <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <p>
          Scoring via een onafhankelijke Claude-rechter + regex-detectie. Laatst gemeten:{" "}
          <span className="font-mono">{formatDate(latestDate)}</span>. Lager scoort soms een team — dat tonen we ook,
          want pretenderen helpt geen enkele professional.
        </p>
      </div>
    </section>
  );
};
